import { useMemo } from 'react';
import { UB_AccountUser } from '../../api/protosCompiled/ub_users/ub_users_pb';
import { PermissionService } from './PermissionService';
import { useFetchUser } from '../../hooks/useFetchUsers';
import { PERMISSIONS } from './constants';
import { useAssemblyStore } from '../../stores/AssemblyStore';

/**
 * React hook for easy permission checking in components
 *
 * @param user - The current user object
 * @returns Object with permission checking methods
 */
export const usePermissions = (user?: UB_AccountUser.AsObject) => {
  // Memoize the permission checking functions to avoid recreating them on every render
  const permissionMethods = useMemo(() => {
    if (!user?.roleid) {
      // Return no-permission functions if no user or role
      return {
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        isPrimaryAdmin: false,
        isAdmin: false,
        canManageUsers: false,
        canPerformEstimating: false,
        canManageAssemblies: false,
        canAccessCloseouts: false,
        canUseDrawingTools: false,
        roleName: 'No Role',
        roleId: null,
      };
    }

    return {
      // Basic permission checks
      hasPermission: (permission: string) =>
        PermissionService.hasPermission(user.roleid, permission),

      hasAnyPermission: (permissions: string[]) =>
        PermissionService.hasAnyPermission(user.roleid, permissions),

      hasAllPermissions: (permissions: string[]) =>
        PermissionService.hasAllPermissions(user.roleid, permissions),

      // Convenience methods for common checks
      isPrimaryAdmin: PermissionService.isPrimaryAdminFromUser(user),
      isAdmin: PermissionService.isAdmin(user.roleid),
      canManageUsers: PermissionService.canManageUsers(user.roleid),
      canPerformEstimating: PermissionService.canPerformEstimating(user.roleid),
      canManageAssemblies: PermissionService.canManageAssemblies(user.roleid),
      canAccessCloseouts: PermissionService.canAccessCloseouts(user.roleid),
      canUseDrawingTools: PermissionService.canUseDrawingTools(user.roleid),

      // Primary admin only methods
      canManageOtherUsers: PermissionService.canManageOtherUsers(user),
      canCreateUsers: PermissionService.canCreateUsers(user),
      canDeleteUsers: PermissionService.canDeleteUsers(user),
      canChangeUserRoles: PermissionService.canChangeUserRoles(user),
      canActivateDeactivateUsers: PermissionService.canActivateDeactivateUsers(user),
      canManageSystemSettings: PermissionService.canManageSystemSettings(user),
      canManageSubscriptions: PermissionService.canManageSubscriptions(user),
      canAccessAnalytics: PermissionService.canAccessAnalytics(user),
      canEditOwnPrimaryAdminStatus: PermissionService.canEditOwnPrimaryAdminStatus(user),

      // User info
      roleName: PermissionService.getRoleName(user.roleid),
      roleId: user.roleid,
    };
  }, [user]);

  return permissionMethods;
};

/**
 * Hook for checking permissions with a specific user (useful for admin interfaces)
 *
 * @param targetUser - The user to check permissions for
 * @returns Object with permission checking methods for the target user
 */
export const usePermissionsForUser = (targetUser?: UB_AccountUser.AsObject) => {
  return usePermissions(targetUser);
};

/**
 * Hook for checking if current user can perform actions on another user
 *
 * @param currentUser - The current logged-in user
 * @param targetUser - The user being acted upon
 * @returns Object with permission checking methods for user management
 */
export const useUserManagementPermissions = (
  currentUser?: UB_AccountUser.AsObject,
  targetUser?: UB_AccountUser.AsObject
) => {
  const currentUserPermissions = usePermissions(currentUser);

  return useMemo(() => {
    if (!currentUser?.roleid) {
      return {
        canEditUser: false,
        canDeleteUser: false,
        canChangeUserRole: false,
        canActivateUser: false,
        isOwnAccount: false,
      };
    }

    const isOwnAccount = targetUser?.user?.id === currentUser.user?.id;

    return {
      // Can edit user if primary admin or editing own account
      canEditUser: currentUserPermissions.isPrimaryAdmin || isOwnAccount,

      // Can delete user if primary admin and not deleting own account
      canDeleteUser: currentUserPermissions.isPrimaryAdmin && !isOwnAccount,

      // Can change user role if primary admin and not changing own role
      canChangeUserRole: currentUserPermissions.isPrimaryAdmin && !isOwnAccount,

      // Can activate/deactivate user if primary admin
      canActivateUser: currentUserPermissions.isPrimaryAdmin,

      // Can edit primary admin status (never allowed for own account)
      canEditPrimaryAdminStatus: currentUserPermissions.isPrimaryAdmin && !isOwnAccount,

      // Is this the current user's own account
      isOwnAccount,
    };
  }, [currentUser, targetUser, currentUserPermissions.isPrimaryAdmin]);
};

/**
 * Hook for handling disable row actions based on assembly type
 *
 * @returns handleDisableRowActions function
 * @example
 * const { handleDisableRowActions } = useAssemblyRowActions();
 * return <div onClick={handleDisableRowActions}>Click me</div>;
 */
export const useAssemblyRowActions = () => {
  const { data: currentUser } = useFetchUser();
  const { hasAnyPermission } = usePermissions(currentUser);
  const currentGlobalAssembly = useAssemblyStore(state => state.currentGlobalAssembly);
  const currentTakeoffAssembly = useAssemblyStore(state => state.currentTakeoffAssembly);

  const handleDisableRowActions = useMemo(() => {
    return () => {
      if (currentGlobalAssembly) {
        return !hasAnyPermission([PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES]);
      } else if (currentTakeoffAssembly) {
        return !hasAnyPermission([PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES]);
      }
      return false;
    };
  }, [hasAnyPermission, currentGlobalAssembly, currentTakeoffAssembly]);

  return { handleDisableRowActions };
};
